import posthog from 'posthog-js'
import type { OrgSchemaType } from '@/types/portal-api.generated'
import type { Member } from '@stytch/nextjs/b2b'
import { errorAnalyticsPayload } from './errorAnalytics'
/**
 * Identifies an authenticated user in PostHog.
 * @param member - Stytch member object.
 */
export function identifyUser(member?: Member) {
  try {
    if (member) {
      const userId = member.member_id
      const email = member.email_address
      const emailVerified = member.email_address_verified
      const mfaEnrolled = member.mfa_enrolled

      posthog.identify(userId, {
        email,
        email_verified: emailVerified,
        mfa_enrolled: mfaEnrolled,
      })
    }
  } catch (error) {
    // Log error but don't throw - analytics shouldn't break the app
    console.error('PostHog identification failed:', errorAnalyticsPayload(error))
  }
}

/**
 * Sets up group tracking for a Patient Portal organization.
 * Client Portal partner/program scoping uses {@link identifyPartnerGroup} instead.
 */
export function identifyGroup(organization: OrgSchemaType) {
  // Only include non-null/undefined properties
  const cleanProperties = Object.fromEntries(
    Object.entries(organization).filter(([_, value]) => value != null),
  )

  posthog.group('client', organization.id, cleanProperties)
}

/**
 * Registers app-level PostHog super properties that should survive identity reset.
 * `posthog.reset()` clears registered properties, so call this after init and after reset.
 */
export function registerAppSuperProperties() {
  // This works because we set APP_VERSION in next.config.js so it's inlined at build time.
  if (process.env.APP_VERSION) {
    posthog.register({ appVersion: process.env.APP_VERSION })
  }
}

/**
 * Resets analytics state on logout
 */
export function resetAnalytics() {
  posthog.reset(true)
  registerAppSuperProperties()
}
